import { LocalSmsTemplate } from './campaign-sms.model';
import { EmailTemplate } from './email-templates.model';

export interface EmailCampaign {
  id?: string;
  organization_id: string;
  name: string;
  description: string;
  goal: EmailCampaignGoal;
  event: EmailCampaignEvent;
  schedule_type: EmailScheduleType;
  spotlight_directory?: {
    id: number;
    name?: string;
  };
  attach_pdf: boolean;
  trigger_conditions: TriggerConditions;
  delivery_conditions: {
    sending_time?: SendingTime;
  };
  category: EmailCategory;
  tag: EmailTag;
  audience_conditions: AudienceConditions;
  campaign_channels: CampaignChannel[];
  groups_count: number;
  add_on: boolean;
  source: boolean;
  source_campaign_id: string;
  source_campaign?: SourceCampaign;
  has_pending_source_updates: boolean;
  all_groups: boolean;
  groups: CampaignGroupCollection[];
  group_collections: CampaignGroupCollection[];
  active: boolean;
  created_at: string;
  updated_at: string;
  email_settings: EmailCampaignSettings;
}

export enum EmailCategory {
  CREDENTIAL_ENGAGEMENT_BOOST = 'credential_engagement_boost',
  MARKETING = 'marketing',
  CREDENTIAL_RENEWAL_GROWTH = 'credential_renewal_growth',
}

export enum EmailTag {
  POPULAR = 'popular',
  TRENDING = 'trending',
}

export interface SourceCampaign {
  add_on: boolean;
  id: string;
  name: string;
}

export enum RendererSection {
  AUDIENCE = 'audience',
  TRIGGER = 'trigger',
  TEMPLATE = 'template',
  SMS_TEMPLATE = 'sms_template',
  SMS_TEMPLATE_PREVIEW = 'sms_template_preview',
}

export type CampaignChannel = EmailCampaignChannel | SmsCampaignChannel;
export type CampaignChannelMerged = EmailCampaignChannel & SmsCampaignChannel;

export interface EmailCampaignChannel {
  id: string;
  channel_type: EmailCampaignChannelType;
  email_template_id: number;
  email_template?: EmailTemplate;
}

export interface SmsCampaignChannel {
  id: string;
  channel_type: EmailCampaignChannelType;
  sms_template_id: string;
  sms_template?: LocalSmsTemplate;
}

export interface CampaignsListActionEvent {
  action: CampaignsListAction;
  campaign?: EmailCampaign;
}

export interface SelectorConfiguration {
  type: RendererSection;
  method: (campaign: EmailCampaign) => boolean;
  isConfigured: boolean;
  iconName: string;
  iconPath: string;
}

export interface CampaignGroupCollection {
  id: string | number;
  name?: string;
  groups_count?: number;
}

export interface TriggerConditions {
  schedule: ScheduleTrigger;
}

export interface SendingTime {
  timezone_name: string;
  days: string[];
  time_range: {
    from: string;
    to: string;
  };
}

export interface ScheduleTrigger {
  scale: number;
  unit: EmailScheduleUnitType;
}

export interface AudienceConditions {
  credentials?: Partial<CredentialAudienceConditions>;
  spotlight_directory?: Partial<SpotlightAudienceConditions>;
}

export interface CredentialAudienceConditions {
  engagement: CredentialEngagement;
  status: CredentialStatus;
  issued_on: IssuedOn;
}

export interface SpotlightAudienceConditions {
  recipient: {
    profile_visibility: boolean;
    profile_completion: {
      min: number;
      max: number;
    };
  };
}

export interface EmailCampaignSettings {
  from_name: string;
  from_email: string;
  reply_to_name: string;
  reply_to_email: string;
  cc_email: string;
  bcc_email: string;
  from_email_verified: boolean;
}

export interface CredentialEngagement {
  recipient_opened: boolean;
  recipient_engaged: boolean;
  linkedin_engaged: boolean;
}

export interface CredentialStatus {
  approve: boolean;
  expired: boolean;
  private: boolean;
  bounced_email: boolean;
}

export interface IssuedOn {
  start: string;
  end: string;
}

export type CampaignDialogData = Pick<
  EmailCampaign,
  'id' | 'name' | 'goal' | 'description' | 'email_settings'
> & {
  showWarningTrigger?: boolean;
  sendingTime?: SendingTime;
};

export enum EmailCampaignChannelType {
  EMAIL = 'email',
  SMS = 'sms',
}

export enum EmailCampaignGoal {
  CREDENTIAL_ENGAGEMENT = 'credential_engagement',
  CREDENTIAL_RENEWALS = 'credential_renewals',
  MARKETING_NEW_COURSES = 'marketing_new_courses',
  PATHWAYS_COMPLETION = 'pathways_completion',
  LIVE_CREDENTIAL_ENGAGEMENT = 'live_credential_engagement',
  SPOTLIGHT_DIRECTORY_ENGAGEMENT = 'spotlight_directory_engagement',
  OTHER = 'other',
}

export enum EmailCampaignEvent {
  CREDENTIAL_PUBLISHED = 'credential_published',
  CREDENTIAL_RENEWED = 'credential_renewed',
  CREDENTIAL_EXPIRED = 'credential_expired',
  LIVE_CREDENTIAL_ACTIVE = 'live_credential_active',
  LIVE_CREDENTIAL_AT_RISK = 'live_credential_at_risk',
  LIVE_CREDENTIAL_LAPSED = 'live_credential_lapsed',
  LIVE_CREDENTIAL_REINSTATED = 'live_credential_reinstated',
  LIVE_CREDENTIAL_RUBRIC_AT_RISK = 'live_credential_rubric_at_risk',
  LIVE_CREDENTIAL_RUBRIC_LAPSED = 'live_credential_rubric_lapsed',
  LIVE_CREDENTIAL_RUBRIC_REINSTATED = 'live_credential_rubric_reinstated',
  SPOTLIGHT_DIRECTORY_PROFILE_CREATED = 'spotlight_directory_profile_created',
  SPOTLIGHT_DIRECTORY_ELIGIBLE_CREDENTIAL_ADDED = 'spotlight_directory_eligible_credential_added',
}

export enum EmailScheduleType {
  INSTANT = 'instant',
  BEFORE = 'before',
  AFTER = 'after',
}

export enum EmailScheduleUnitType {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum CampaignsListAction {
  CREATE = 'create',
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  TOGGLE_STATE = 'toggle_state',
  DELETE = 'delete',
}

export enum CampaignBuilderStatus {
  SAVED = 'saved',
  UNSAVED_CHANGES = 'unsaved_changes',
  NO_CHANGES = 'no_changes',
}

export enum CampaignBuilderHeaderAction {
  OPEN_GUIDE = 'open_guide',
  OPEN_SETTINGS = 'open_settings',
  SAVE = 'save',
  SAVE_ACTIVATE = 'save_activate',
  DISCARD = 'discard',
  DEACTIVATE = 'deactivate',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
}

export interface EmailCampaignsBasicAnalytics {
  number_of_sent_emails: number;
  number_of_opened_emails: number;
  number_of_bounced_emails: number;
  number_of_clicked_emails: number;
  using_custom_smtp?: boolean;
  campaigns_breakdown?: EmailCampaignsBreakdowns[];
}

export interface EmailCampaignsBreakdowns {
  campaign_name: string;
  campaign_id: string;
  groups: EmailCampaignsBasicAnalyticsGroups[];
  number_of_sent_emails: number;
  number_of_opened_emails: number;
  number_of_bounced_emails: number;
  number_of_clicked_emails: number;
  using_custom_smtp?: boolean;
}

export interface EmailCampaignsBasicAnalyticsGroups {
  name: string;
}
